/**
 * 兩條橫幅：手動公告與「有新版」提示。
 *
 * 兩者要回答的是同一種問題——**現在該不該打擾使用者**——所以放在一起，
 * 而且都是純函式：判斷不讀 localStorage 也不讀 window，那些由呼叫端傳入。
 */

export interface Notice {
  /**
   * 換一則公告就換一個 id。
   *
   * 關掉的紀錄存的是 id 而不是布林——存布林的話，使用者關掉某一則之後，
   * 之後每一則新公告都會被一起擋住。
   */
  id: string;
  text: string;
  /**
   * 只給從主畫面開啟的人看。
   *
   * 需要重裝的事（換網域、改圖示與 App 名稱）只有已經安裝的人要處理，
   * 用瀏覽器逛的人看到只會困惑。
   */
  standaloneOnly: boolean;
  /** 公告附的連結。賽季開賽那則要帶人去成績單與新競猜。 */
  links?: readonly ("season-report" | "prediction")[];
}

/**
 * 目前要顯示的公告。**改這裡就等於發布或撤下公告**，不公告時是 null。
 *
 * 需要重裝的情況其實只有三種：換網域、改 App 名稱或圖示（iOS 在安裝那一刻
 * 就把兩者烙進主畫面，之後改 manifest 對已安裝的沒有作用）、以及 SW 卡死。
 * 一般改版不需要——程式碼由 Service Worker 自己換掉。
 *
 * **換網域的話這條救不了你**：已安裝的捷徑指向舊網址，公告要放在舊網域上。
 */
export const NOTICE: Notice | null = {
  id: "season-2-start",
  text: "2.0 開賽：台北馬賽季開始了，競猜也換成新題目。1.0 的成績都留著。",
  standaloneOnly: false,
  links: ["season-report", "prediction"]
};

export interface NoticeContext {
  /** 使用者關掉過的公告 id。沒關過是 null。 */
  dismissedId: string | null;
  /** 是不是從主畫面開啟的（display-mode: standalone）。 */
  standalone: boolean;
}

export function noticeToShow(notice: Notice | null, context: NoticeContext): Notice | null {
  if (!notice) return null;
  if (notice.id === context.dismissedId) return null;
  if (notice.standaloneOnly && !context.standalone) return null;
  return notice;
}

/**
 * 手上這份程式碼是不是舊的。
 *
 * iOS 從主畫面開 PWA 是**恢復凍結的頁面**而不是重新載入，所以只要使用者沒有
 * 把 App 滑掉，部署了新版他仍然在跑舊的 JS——資料會更新，程式碼不會。
 *
 * 任一邊拿不到指紋就回 false：離線或那支檔案掛掉的時候，寧可不提示，
 * 也不要跳一條點了也沒用的提示。
 */
export function hasNewBuild(current: string, latest: string | null | undefined): boolean {
  if (!current || !latest) return false;
  return current !== latest;
}
